import { useState } from 'react'
import type { ReactNode } from 'react'
import { Check, Copy, Loader2, Sparkles } from 'lucide-react'
import Markdown from '@/components/ui/Markdown'
import { cn } from '@/lib/cn'
import { toolLabel } from './toolLabels'

export interface ToolTrailItem {
  name: string
  status: 'running' | 'done'
}

/**
 * The model's reasoning while it works, collapsed. Also the only "still
 * working" signal: "Working" with nothing to open yet, "Thinking" once there
 * is reasoning to read.
 */
function Thinking({ text, streaming }: { text?: string; streaming: boolean }) {
  if (!text && !streaming) return null

  const label = (word: string) => (
    <span className="inline-flex items-center gap-1.5 font-medium">
      {word}
      {streaming && <Loader2 className="size-3 animate-spin text-primary" />}
    </span>
  )

  if (!text) {
    return <div className="mb-2 text-xs text-foreground/60">{label('Working')}</div>
  }

  return (
    <details className="mb-2 rounded-lg border border-foreground/10 bg-foreground/[0.03] px-3 py-2 text-xs text-foreground/60">
      <summary className="cursor-pointer select-none">{label('Thinking')}</summary>
      <p className="mt-2 max-h-60 overflow-y-auto whitespace-pre-wrap leading-relaxed">{text}</p>
    </details>
  )
}

/** Which lookups ran: live chips while working, a collapsed list after. */
function ToolTrail({ tools }: { tools?: ToolTrailItem[] }) {
  if (!tools?.length) return null

  if (tools.some((t) => t.status === 'running')) {
    return (
      <div className="mb-2 flex flex-wrap gap-1.5">
        {tools.map((tool, i) => (
          <span
            key={`${tool.name}-${i}`}
            className="inline-flex items-center gap-1.5 rounded-full border border-foreground/15 bg-background px-2.5 py-0.5 text-xs text-foreground/70"
          >
            {tool.status === 'running' ? (
              <Loader2 className="size-3 animate-spin text-primary" />
            ) : (
              <Check className="size-3 text-success" />
            )}
            {toolLabel(tool.name)}
          </span>
        ))}
      </div>
    )
  }

  return (
    <details className="mb-2 text-xs text-foreground/60">
      <summary className="cursor-pointer select-none">
        Used {tools.length} {tools.length === 1 ? 'source' : 'sources'}
      </summary>
      <ul className="mt-1.5 space-y-0.5 pl-4">
        {tools.map((tool, i) => (
          <li key={`${tool.name}-${i}`} className="list-disc">
            {toolLabel(tool.name)}
          </li>
        ))}
      </ul>
    </details>
  )
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false)

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1500)
    } catch {
      // Clipboard blocked in some embedded browsers; not worth a toast.
    }
  }

  return (
    <button
      type="button"
      onClick={copy}
      className="-m-1 inline-flex items-center gap-1 rounded p-1 text-xs text-foreground/50 hover:bg-foreground/5 hover:text-foreground"
    >
      {copied ? <Check className="size-3" /> : <Copy className="size-3" />}
      {copied ? 'Copied' : 'Copy'}
    </button>
  )
}

/** One bubble. The admin's questions sit right; answers run full width because they carry tables. */
export default function ChatMessage({
  role,
  content,
  tools,
  reasoning,
  streaming = false,
  children,
}: {
  role: 'user' | 'assistant'
  content?: string | null
  tools?: ToolTrailItem[]
  reasoning?: string
  streaming?: boolean
  children?: ReactNode
}) {
  if (role === 'user') {
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] whitespace-pre-wrap break-words rounded-2xl rounded-br-sm border border-primary/20 bg-primary/10 px-4 py-2.5 text-sm text-foreground">
          {content}
        </div>
      </div>
    )
  }

  return (
    <div className="flex gap-3">
      {/* The rainbow rim (.ai-ring) only on the answer still being written, so it
          doubles as "working"; a long chat of spinning avatars would be noise. */}
      {streaming ? (
        <span className="ai-ring mt-0.5 inline-flex size-7 shrink-0">
          <span className="flex size-full items-center justify-center rounded-full bg-surface text-primary">
            <Sparkles className="size-3.5" />
          </span>
        </span>
      ) : (
        <div className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full border border-primary/30 bg-primary/10 text-primary">
          <Sparkles className="size-3.5" />
        </div>
      )}

      <div className="min-w-0 flex-1">
        <ToolTrail tools={tools} />
        <Thinking text={reasoning} streaming={streaming && !content} />

        {content && <Markdown>{content}</Markdown>}

        {children}

        {content && !streaming && (
          <div className={cn('mt-1.5 flex items-center gap-1')}>
            <CopyButton text={content} />
          </div>
        )}
      </div>
    </div>
  )
}
